import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { TabItem, Tabs } from '@adeccoux/tag-ds';
import { Box, Grid } from '@mui/material';
import { RootState } from '@store/rootReducer';

import { MailHistoryType } from '../BulkMailInterfaces';

import EmailComposer from './EmailComposer';

interface BulkMailListProps {
  selectedMailType: { mailTemplateId: string | null; id: string | null };
  setSelectedMailType: (mail: { mailTemplateId: null | string; id: null | string }) => void;
  activeTab: string;
  setActiveTab: (value: MailHistoryType) => void;
  setShowCustomEmail: (value: boolean) => void;
  multipleJourniesSelected?: boolean;
  mailSubject: string;
  setMailSubject: (value: string) => void;
  mailBody: string;
  setMailBody: (value: string) => void;
}

const BulkMailList: React.FC<BulkMailListProps> = ({
  selectedMailType,
  setSelectedMailType,
  activeTab,
  setActiveTab,
  setShowCustomEmail,
  multipleJourniesSelected,
  mailSubject,
  setMailSubject,
  mailBody,
  setMailBody,
}) => {
  const { mails } = useSelector((state: RootState) => state.oneOnboarding);
  const { t } = useTranslation(['recruiter', 'recruiter-deprecated']);
  const mailTypes = {
    manual: [
      ...new Map(
        mails
          .filter((m: any) => m.ruleType === 'Manual' && m.mailTemplateId !== 'Custom')
          .map((item: any) => [item.mailTemplateId.replace(/\s/g, ''), item]),
      ).values(),
    ],
    automatic: [
      ...new Map(
        mails.filter((m) => m.ruleType !== 'Manual').map((item) => [item.mailTemplateId.replace(/\s/g, ''), item]),
      ).values(),
    ],
    custom: [],
  };

  /** TODO: once we have the custom email template for DIS AG branding we should remove this code */
  const isDisAgTenant = mails.some((mail) => mail.tenantId === 131);

  const tabChangeHandler = (selectedTab: any) => {
    setActiveTab(selectedTab);
    setMailSubject('');
    setMailBody('');
    if (selectedTab === MailHistoryType.CUSTOM) {
      const customTemplte = mails.find((m: any) => m.mailTemplateId === 'Custom');
      setSelectedMailType({
        mailTemplateId: customTemplte.mailTemplateId,
        id: customTemplte.id,
      });
    } else {
      setSelectedMailType({
        mailTemplateId: null,
        id: null,
      });
    }
  };

  return (
    <div className="tag-ds ">
      <Tabs className="feature-mail-overview__tabs-container" onChangeTab={tabChangeHandler}>
        <TabItem id={MailHistoryType.AUTOMATIC} title={t('recruiter:GENERAL.MAILS.Automatic')}>
          <div className="checkbox-group-container">
            {!multipleJourniesSelected &&
              mailTypes['automatic'].map(({ mailTemplateId, id }) => {
                const translate = mailTemplateId.replace(/\s/g, '');
                return (
                  <div
                    key={mailTemplateId + id}
                    className="checkbox-container feature-mail-overview__item"
                    onClick={() => {
                      setShowCustomEmail(mailTemplateId === 'Custom');
                      setSelectedMailType({ mailTemplateId, id });
                    }}
                  >
                    <input
                      id={mailTemplateId}
                      type="radio"
                      checked={selectedMailType.mailTemplateId === mailTemplateId}
                      onChange={() => setSelectedMailType({ mailTemplateId, id })}
                    />
                    <label htmlFor={mailTemplateId}>
                      {t('recruiter-deprecated:CANDIDATE_PROFILE.Mails.' + translate)}
                    </label>
                  </div>
                );
              })}
          </div>
        </TabItem>
        <TabItem
          collapsed={activeTab === 'Custom'}
          id={MailHistoryType.MANUAL}
          title={t('recruiter:GENERAL.MAILS.Manual')}
        >
          <div className="checkbox-group-container">
            {!multipleJourniesSelected &&
              mailTypes['manual'].map(({ mailTemplateId, id, label }) => {
                const translate = mailTemplateId.replace(/\s/g, '');
                return (
                  <div
                    key={mailTemplateId + id}
                    className="checkbox-container feature-mail-overview__item"
                    onClick={() => {
                      setSelectedMailType({ mailTemplateId, id });
                      setShowCustomEmail(mailTemplateId === 'Custom');
                    }}
                  >
                    <input
                      id={mailTemplateId}
                      type="radio"
                      checked={selectedMailType.mailTemplateId === mailTemplateId}
                      onChange={() => setSelectedMailType({ mailTemplateId, id })}
                    />
                    <label htmlFor={mailTemplateId}>
                      {t('recruiter-deprecated:CANDIDATE_PROFILE.Mails.' + translate)}
                    </label>
                  </div>
                );
              })}
          </div>
        </TabItem>
        <TabItem
          collapsed={activeTab === 'test'}
          id={MailHistoryType.CUSTOM}
          title={t('recruiter:GENERAL.MAILS.Custom')}
          style={{ display: isDisAgTenant ? 'none' : 'block' }}
        >
          <div className="checkbox-group-container">
            <Box sx={{ height: '100vh', overflow: 'hidden' }}>
              <Grid container sx={{ height: '100%' }}>
                <EmailComposer
                  rowsCount={8}
                  mailSubject={mailSubject}
                  setMailSubject={setMailSubject}
                  mailBody={mailBody}
                  setMailBody={setMailBody}
                />
              </Grid>
            </Box>
          </div>
        </TabItem>
      </Tabs>
    </div>
  );
};

export default BulkMailList;
